/**
 * Account Manager
 *
 * Manages multiple logged-in accounts in localStorage.
 * Supports both switching between accounts and switching companies within an account.
 */

import type { User, Company } from '../services/api/auth';

/**
 * Company that a user can access
 */
export interface AccessibleCompany {
  id: string;
  name: string;
  slug: string;
  type: string;
  status: string;
  isPrimary: boolean;
}

/**
 * Session data for a single account
 */
export interface AccountSession {
  accessToken: string;
  refreshToken: string;
  expiresAt: number;
  user: User;
  activeCompanyId: string;
  activeCompany: Company | null;
  accessibleCompanies: AccessibleCompany[];
}

/**
 * Storage structure for all accounts
 */
export interface AccountsStorage {
  [email: string]: AccountSession;
}

const ACCOUNTS_KEY = 'accounts';
const ACTIVE_ACCOUNT_KEY = 'activeAccountEmail';

/**
 * Account Manager - manages multiple logged-in accounts
 */
export const accountManager = {
  /**
   * Get all stored accounts
   */
  getAllAccounts(): AccountsStorage {
    try {
      const stored = localStorage.getItem(ACCOUNTS_KEY);
      return stored ? JSON.parse(stored) : {};
    } catch {
      return {};
    }
  },

  /**
   * Get active account email
   */
  getActiveAccountEmail(): string | null {
    return localStorage.getItem(ACTIVE_ACCOUNT_KEY);
  },

  /**
   * Get active account session
   */
  getActiveSession(): AccountSession | null {
    const email = this.getActiveAccountEmail();
    if (!email) return null;

    const accounts = this.getAllAccounts();
    return accounts[email] || null;
  },

  /**
   * Get all accounts as array (for UI display)
   */
  getAllAccountsList(): Array<{ email: string; session: AccountSession }> {
    const accounts = this.getAllAccounts();
    return Object.entries(accounts).map(([email, session]) => ({
      email,
      session: session as AccountSession,
    }));
  },

  /**
   * Add or update an account
   */
  setAccount(email: string, session: AccountSession): void {
    const accounts = this.getAllAccounts();
    accounts[email] = session;
    localStorage.setItem(ACCOUNTS_KEY, JSON.stringify(accounts));
  },

  /**
   * Set active account
   */
  setActiveAccount(email: string): void {
    const accounts = this.getAllAccounts();
    const session = accounts[email];

    if (!session) {
      throw new Error('Account not found');
    }

    localStorage.setItem(ACTIVE_ACCOUNT_KEY, email);
  },

  /**
   * Remove an account
   */
  removeAccount(email: string): void {
    const accounts = this.getAllAccounts();
    delete accounts[email];
    localStorage.setItem(ACCOUNTS_KEY, JSON.stringify(accounts));

    // If removing active account, switch to another or clear
    const activeEmail = this.getActiveAccountEmail();
    if (activeEmail === email) {
      const remainingEmails = Object.keys(accounts);
      if (remainingEmails.length > 0) {
        this.setActiveAccount(remainingEmails[0]);
      } else {
        this.clearAll();
      }
    }
  },

  /**
   * Remove all accounts (full logout)
   */
  clearAll(): void {
    localStorage.removeItem(ACCOUNTS_KEY);
    localStorage.removeItem(ACTIVE_ACCOUNT_KEY);
  },

  /**
   * Get account count
   */
  getAccountCount(): number {
    return Object.keys(this.getAllAccounts()).length;
  },

  /**
   * Check if an account exists
   */
  hasAccount(email: string): boolean {
    const accounts = this.getAllAccounts();
    return email in accounts;
  },

  /**
   * Update company context for active account.
   *
   * `effectivePermissions`, when provided, are the permissions the user actually
   * has in the newly-active company (returned by /switch-company). We store them
   * on `user.permissions` so the frontend (nav, route guards) reflects the active
   * company's permissions rather than the stale home-company ones. Without this,
   * a user switched into a company where they have fewer permissions would still
   * see menu items they can't use (which then 403 on the backend).
   *
   * NOTE: only `user.permissions` is updated — `user.companyId` is intentionally
   * left as the user's PRIMARY company, which switchAccount relies on to decide
   * whether a stored session is parked in a secondary company.
   */
  updateActiveCompany(
    companyId: string,
    accessToken: string,
    refreshToken: string,
    expiresAt: number,
    company: Company | null,
    effectivePermissions?: string[]
  ): void {
    const email = this.getActiveAccountEmail();
    if (!email) return;

    const accounts = this.getAllAccounts();
    if (accounts[email]) {
      accounts[email] = {
        ...accounts[email],
        accessToken,
        refreshToken,
        expiresAt,
        activeCompanyId: companyId,
        activeCompany: company,
        user: effectivePermissions
          ? { ...accounts[email].user, permissions: effectivePermissions }
          : accounts[email].user,
      };
      localStorage.setItem(ACCOUNTS_KEY, JSON.stringify(accounts));
    }
  },

  /**
   * Update accessible companies for active account
   */
  updateAccessibleCompanies(companies: AccessibleCompany[]): void {
    const email = this.getActiveAccountEmail();
    if (!email) return;

    const accounts = this.getAllAccounts();
    if (accounts[email]) {
      accounts[email] = {
        ...accounts[email],
        accessibleCompanies: companies,
      };
      localStorage.setItem(ACCOUNTS_KEY, JSON.stringify(accounts));
    }
  },

  /**
   * Update tokens for active account (e.g., after refresh)
   */
  updateTokens(accessToken: string, expiresAt: number): void {
    const email = this.getActiveAccountEmail();
    if (!email) return;

    const accounts = this.getAllAccounts();
    if (accounts[email]) {
      accounts[email] = {
        ...accounts[email],
        accessToken,
        expiresAt,
      };
      localStorage.setItem(ACCOUNTS_KEY, JSON.stringify(accounts));
    }
  },

  /**
   * Check if active session token is expired
   */
  isActiveSessionExpired(): boolean {
    const session = this.getActiveSession();
    if (!session) return true;
    return Date.now() > session.expiresAt;
  },
};
